import React, { useState } from 'react';
import { generateRelationshipHarmony } from '../services/geminiService';
import { RelationshipHarmonyGuide } from '../types';
import { Users, RefreshCw, Heart, Sparkles } from 'lucide-react';
import { useUser } from '../context/UserContext';

const SlowDownConnect: React.FC = () => {
  const [conflict, setConflict] = useState('');
  const [guide, setGuide] = useState<RelationshipHarmonyGuide | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp } = useUser();

  const handleConnect = async () => {
    if (!conflict.trim()) return;
    setLoading(true);
    setGuide(null);
    try {
      const data = await generateRelationshipHarmony(conflict);
      setGuide(data);
      addXp(40, 'Restored Harmony');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-pink-50 text-pink-600 rounded-full mb-4">
          <Users size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Relationship Harmony</h1>
        <p className="text-nunchi-600">
          "The moment you lower your guard and open your heart, <br/>the other person will do the same."
        </p>
      </header>

      {!guide && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                Describe a friction or misunderstanding
            </label>
            <textarea
              value={conflict}
              onChange={(e) => setConflict(e.target.value)}
              placeholder="e.g., My partner is always late and it annoys me, I feel judged by my colleague..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-pink-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleConnect}
              disabled={!conflict.trim()}
              className="w-full bg-pink-700 text-white py-4 rounded-xl font-bold hover:bg-pink-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Heart size={18} /> Soften the Edge
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-pink-100 border-t-pink-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-pink-700">Opening the heart...</p>
        </div>
      )}

      {guide && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-pink-50 p-6 rounded-xl border border-pink-100">
                <h3 className="text-pink-700 font-bold uppercase tracking-wider text-xs mb-2 flex items-center gap-2">
                    <Sparkles size={14} /> Empathy First
                </h3>
                <p className="text-nunchi-800 font-medium leading-relaxed">{guide.empathy}</p>
            </div>

            <div className="bg-white border border-nunchi-200 rounded-2xl p-8 shadow-sm">
                 <h3 className="text-nunchi-400 uppercase tracking-widest text-xs font-bold mb-3">Gentle Advice</h3>
                 <p className="text-lg leading-relaxed font-serif text-nunchi-800 mb-6">
                     "{guide.advice}"
                 </p>
                 <div className="bg-white p-4 rounded-lg text-center border border-nunchi-100 shadow-inner">
                     <p className="text-xs text-nunchi-400 uppercase font-bold mb-1">Self-Reflection</p>
                     <p className="text-sm font-medium text-nunchi-600">{guide.reflection}</p>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setGuide(null); setConflict(''); }}
                  className="text-pink-600 hover:text-pink-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Heal Another Bond
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default SlowDownConnect;